import {ColorBlendColor, normal} from 'color-blend';
import _, {isEqual} from 'lodash';
import {parseToRgb, rgba} from 'polished';
import {RgbaColor, RgbColor} from 'polished/lib/types/color';

import {collectEnumValues, makeEnumTypeGuard} from '../../../helpers/src/typescript/enumHelpers';
import {PaletteColorsEnum} from './paletteModel';

/** Blends the background color with the foreground color. An equivalent to the `blend()` method of Stylus. */
export function blendColors(background: string, foreground: string) {
  const parsedBackground = polishedToColorBlend(parseToRgb(background));
  const parsedForeground = polishedToColorBlend(parseToRgb(foreground));

  return rgba(colorBlendToPolished(normal(parsedBackground, parsedForeground)));
}

function polishedToColorBlend(color: RgbColor | RgbaColor): ColorBlendColor {
  return {
    r: color.red,
    g: color.green,
    b: color.blue,
    a: isRgbaColor(color) ? color.alpha : 1,
  };
}

function colorBlendToPolished(color: ColorBlendColor): RgbaColor {
  return {
    red: color.r,
    green: color.g,
    blue: color.b,
    alpha: color.a,
  };
}

function isRgbaColor(src: RgbColor | RgbaColor): src is RgbaColor {
  return (src as any).alpha;
}

function toRgbaColor(color: RgbColor | RgbaColor): RgbaColor {
  return isRgbaColor(color) ? color : {...color, alpha: 1};
}

/** Try to parse a color. Return undefined if we fail. */
export function tryParseColor(src?: string | null): RgbColor | RgbaColor | undefined {
  if (!src) {
    return undefined;
  }

  try {
    return parseToRgb(src);
  } catch {
    return undefined;
  }
}

export function colorIsEqual(color1: RgbColor | RgbaColor, color2: RgbColor | RgbaColor) {
  return isEqual(toRgbaColor(color1), toRgbaColor(color2));
}

export const isPaletteColor = makeEnumTypeGuard(PaletteColorsEnum);

const paletteColors = collectEnumValues(PaletteColorsEnum);
export function colorForInteger(src: number) {
  return paletteColors[src % paletteColors.length];
}

const paletteColorsWithoutBlue = _(paletteColors).without(PaletteColorsEnum.BLUE).value();
export function colorForTeammateId(src: number) {
  const finalColor = paletteColors[src % paletteColors.length];

  // Exclude blue from teammate avatars because it blends in with the blue
  // highlight of selected P2 items.
  if (finalColor === PaletteColorsEnum.BLUE) {
    return paletteColorsWithoutBlue[src % paletteColorsWithoutBlue.length];
  }

  return finalColor;
}

/**
 * Excludes yellow, blue, teal, and grey because they blend in against a light blue background.
 */
const companyPaletteColors = _(paletteColors)
  .without(PaletteColorsEnum.YELLOW, PaletteColorsEnum.BLUE, PaletteColorsEnum.TEAL, PaletteColorsEnum.GREY)
  .value();
export function colorForCompanyId(src: number) {
  const finalColor = companyPaletteColors[src % companyPaletteColors.length];

  return finalColor;
}
